import { AlertTriangle, BadgeCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * One fine tied to a party by an extractor, rendered the same way everywhere.
 *
 * Three pages listed the same `decision_fine_attributions` rows and each drew
 * a different subset of what a row carries: the case page quoted the verified
 * span and flagged an unverified one but never said which extractor produced
 * the row; the sanctions page added the pattern badge; the party page showed
 * the extractor and prompt and neither the verified quote's badge nor the
 * pattern badge -- so a reader moving between them saw one attribution mean
 * three different things. One component, so a fourth caller cannot drift.
 *
 * What every row says, in order: what it is about (`subject` -- the party on
 * the case and sanctions pages, the case on the party page), the amount, any
 * secondary line the caller needs (case number and item on the sanctions
 * page), the pattern badge when a regex produced it, the evidence -- a
 * verified span quoted, an unverified one flagged with its text WITHHELD by
 * the API, because a span that failed its own substring check is not a
 * quotation -- and the extractor and prompt that produced it.
 *
 * `currency` is passed in rather than built here: the sanctions page rounds
 * to whole reais and the others do not, and that is each page's call.
 */
export interface AttributedFineLike {
  amount_brl: number | null;
  evidence_span: string | null;
  evidence_verified: boolean;
  model_name: string | null;
  prompt_version: string | null;
  from_pattern: boolean;
}

export function AttributedFineRow({
  fine,
  subject,
  secondary,
  currency,
}: {
  fine: AttributedFineLike;
  subject: ReactNode;
  secondary?: ReactNode;
  currency: Intl.NumberFormat;
}) {
  const { t } = useTranslation(['cade']);
  return (
    <>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {subject}
        <span className="shrink-0 tabular-nums font-semibold text-foreground">
          {fine.amount_brl === null ? '—' : currency.format(fine.amount_brl)}
        </span>
      </div>
      {secondary || fine.from_pattern ? (
        <div className="mt-1 flex flex-wrap items-baseline gap-2 text-muted-foreground">
          {secondary}
          {fine.from_pattern ? (
            <span className="rounded border border-amber-500/50 bg-amber-500/10 px-1.5 py-0.5 text-amber-700 dark:text-amber-400">
              {t('cade:sanctions.provenance.pattern')}
            </span>
          ) : null}
        </div>
      ) : null}
      {fine.evidence_verified && fine.evidence_span ? (
        <p className="mt-1.5 flex items-start gap-1.5 border-l-2 pl-2 text-muted-foreground">
          <BadgeCheck className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          <span className="italic">{fine.evidence_span}</span>
        </p>
      ) : (
        <p className="mt-1.5 flex items-start gap-1.5 text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          <span>{t('cade:case.spanUnverified')}</span>
        </p>
      )}
      <p className="mt-1 text-muted-foreground">
        {t('cade:case.attribution.provenance', {
          model: fine.model_name ?? '—',
          prompt: fine.prompt_version ?? '—',
        })}
      </p>
    </>
  );
}
